/**
 * Code 128, set B — the barcode printed on TezGo's own labels (a Bag's code;
 * ticket 33, spec §26). Every scanner reads it, and set B covers the printable
 * ASCII our codes are made of (`M-123456`). A pure function: the text in, the
 * widths of its bars and spaces out, so any renderer (SVG on the label) draws it.
 *
 * The symbol table is the standard one (ISO/IEC 15417): each entry is the widths,
 * in modules, of bar, space, bar, space, bar, space — the stop adds a last bar.
 */

// prettier-ignore
const PATTERNS = [
	'212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
	'221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
	'221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
	'212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
	'231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
	'231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
	'314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
	'112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
	'111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
	'214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
	'114131', '311141', '411131', '211412', '211214', '211232', '2331112',
] as const

const START_B = 104
const STOP = 106
/** The quiet zone a scanner needs on each side, in modules. */
export const QUIET_ZONE = 10

/** Whether `text` can be written in set B: printable ASCII, space to `~`. */
export function isCode128B(text: string): boolean {
	return text.length > 0 && [...text].every((c) => c.charCodeAt(0) >= 32 && c.charCodeAt(0) <= 126)
}

/** The symbol values of `text` in set B: start, the characters, the check, stop. */
export function code128Symbols(text: string): number[] {
	if (!isCode128B(text)) throw new Error(`Code 128 B cannot write "${text}"`)
	const values = [...text].map((c) => c.charCodeAt(0) - 32)
	const check = values.reduce((sum, v, i) => sum + v * (i + 1), START_B) % 103
	return [START_B, ...values, check, STOP]
}

/**
 * The widths, in modules, of the bars and spaces of `text`, starting with a bar
 * and alternating — the quiet zones not included.
 */
export function code128Widths(text: string): number[] {
	return code128Symbols(text).flatMap((s) => [...PATTERNS[s]].map(Number))
}
